// Shared date helpers for the admin upload pages. The backend serializes Instant via
// Jackson's JavaTimeModule which, under the default WRITE_DATES_AS_TIMESTAMPS=true,
// returns one of: an [year, month, day, ...] array (LocalDateTime), an array of
// [seconds, nanos] (Instant in some configurations), a single epoch-seconds number with
// fractional nanos, or — when the contract is explicit — an ISO-8601 string. `new Date()`
// alone treats a bare epoch-seconds number as milliseconds, which renders any recent
// timestamp as "1970-01-2x" / "~20k days ago" in the UI. This helper normalises every
// shape into a JS Date or null.

export const toDate = (value) => {
    if (value == null) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (Array.isArray(value)) {
        return new Date(
            value[0],
            (value[1] || 1) - 1,
            value[2] || 1,
            value[3] || 0,
            value[4] || 0,
            value[5] || 0,
            Math.floor((Number(value[6]) || 0) / 1_000_000)
        );
    }

    if (typeof value === "number") {
        const ms = value < 1e11 ? value * 1000 : value;
        return new Date(ms);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatRelative = (value) => {
    const date = toDate(value);
    if (!date) return "\u2014";

    const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h ago`;
};

export const formatAbsolute = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleString() : "\u2014";
};

export const formatCalendarDate = (value) => {
    const date = toDate(value);
    return date
        ? new Intl.DateTimeFormat("en-GB", {
            timeZone: "UTC",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(date)
        : "\u2014";
};

export const formatDurationMs = (ms) => {
    if (ms == null || ms < 0) return "\u2014";
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
};